import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from './AuthContext';
import deviceService from '../services/device/deviceService';
import deviceRegistrationService from '../services/device/deviceRegistrationService';
import pushNotificationService from '../services/pushNotifications/pushNotificationService';
import notificationNavigationService from '../services/pushNotifications/notificationNavigationService';
import {
  DEVICE_LIMIT_REACHED_MESSAGE,
  isDeviceLimitReachedError,
} from '../utils/apiErrors';

/**
 * Mobile Device Context
 *
 * Centralizuje native inicijalizaciju i push registraciju:
 *
 *   native start -> deviceId -> login(deviceId) -> authenticated
 *        -> permission -> Capacitor register() -> push token
 *        -> POST /api/mobile-devices/register
 *
 * Web putanja je potpuno no-op: nijedan native API se ne poziva, a jedina
 * obaveza web-a ostaje da prosledi `navigate` navigacionom servisu.
 */

const MobileDeviceContext = createContext(null);

/**
 * Hook za pristup native/mobile stanju i push event-ima.
 * @throws {Error} Ako se koristi van MobileDeviceProvider-a
 */
export const useMobileDevice = () => {
  const context = useContext(MobileDeviceContext);
  if (!context) {
    throw new Error('useMobileDevice must be used within a MobileDeviceProvider');
  }
  return context;
};

export const MobileDeviceProvider = ({ children }) => {
  const isNative = Capacitor.isNativePlatform();
  const { isAuthenticated } = useAuth();

  // Ovaj provajder MORA biti renderovan unutar <Router>-a (vidi App.js):
  // useNavigate() van Router context-a ruši ceo React root. Navigacioni servis
  // ne koristi React hook - `navigate` mu se predaje iz ove komponente.
  const navigate = useNavigate();

  const isAuthenticatedRef = useRef(isAuthenticated);
  const registeredForSessionRef = useRef(false);
  const pendingPushTokenRef = useRef(null);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Router je jedini izvor navigacije - servis ga dobija jednom.
  useEffect(() => {
    notificationNavigationService.setNavigationHandler(navigate);
    return () => notificationNavigationService.setNavigationHandler(null);
  }, [navigate]);

  // Notification tap se izvršava tek kada je korisnik prijavljen i router
  // spreman. Cold start akcija se čuva u notificationNavigationService-u.
  useEffect(() => {
    notificationNavigationService.setNavigationReady(isAuthenticated);
    return () => notificationNavigationService.setNavigationReady(false);
  }, [isAuthenticated]);

  /**
   * Push token -> backend registracija. Poziva se samo kada postoji validan
   * access token (isAuthenticated), nikada pre prijave.
   */
  const registerPushToken = useCallback(
    async (pushToken) => {
      if (!isNative || !pushToken) return;

      if (!isAuthenticatedRef.current) {
        // Token je stigao pre prijave (npr. rani listener) - sačekaj login.
        pendingPushTokenRef.current = pushToken;
        return;
      }

      try {
        await deviceRegistrationService.registerCurrentDevice(pushToken);
      } catch (error) {
        if (isDeviceLimitReachedError(error)) {
          // Registracija u pozadini ne prekida korišćenje aplikacije; poruka je
          // pripremljena i za budući ekran aktivnih uređaja.
          console.warn(DEVICE_LIMIT_REACHED_MESSAGE);
        } else {
          console.error(
            'Registracija mobilnog uređaja nije uspela.',
            error?.message || error
          );
        }
      }
    },
    [isNative]
  );

  // Token može da stigne pre nego što se React mountuje - servis tada odmah
  // poziva handler sa poslednjim tokenom.
  useEffect(() => {
    if (!isNative) return undefined;
    return pushNotificationService.onRegistrationToken(registerPushToken);
  }, [isNative, registerPushToken]);

  /**
   * Native bootstrap i registracija nakon prijave.
   * `registeredForSessionRef` čini registraciju idempotentnom (uključujući
   * React StrictMode dvostruko mountovanje).
   */
  useEffect(() => {
    if (!isNative) return undefined;

    deviceService.initialize().catch(() => {});

    if (!isAuthenticated) {
      // Logout samo gasi auth sesiju - deviceId i device registracija ostaju.
      // Resetuje se lokalna deduplikacija da bi sledeća prijava (moguće drugog
      // zaposlenog na istom uređaju) ponovo registrovala isti deviceId.
      registeredForSessionRef.current = false;
      deviceRegistrationService.resetSession();
      return undefined;
    }

    if (registeredForSessionRef.current) return undefined;
    registeredForSessionRef.current = true;

    const pendingToken = pendingPushTokenRef.current;
    pendingPushTokenRef.current = null;
    if (pendingToken) {
      registerPushToken(pendingToken);
    }

    pushNotificationService
      .ensureRegistered()
      .then(() => {
        const token = pushNotificationService.getLastRegistrationToken();
        if (token) {
          registerPushToken(token);
        }
      })
      .catch((error) => {
        // ensureRegistered interno hvata greške; ovo je samo sigurnosna mreža.
        console.warn('Push registracija nije uspela.', error?.message || error);
      });

    return undefined;
  }, [isAuthenticated, isNative, registerPushToken]);

  const value = useMemo(
    () => ({
      isNativePlatform: isNative,
      subscribeToNotificationReceived: pushNotificationService.onNotificationReceived,
      subscribeToNotificationAction: pushNotificationService.onNotificationAction,
    }),
    [isNative]
  );

  return (
    <MobileDeviceContext.Provider value={value}>{children}</MobileDeviceContext.Provider>
  );
};

export default MobileDeviceContext;
